import { createTableLinkWithOpenNewTabColumn } from '@common/tableColumn.services';
import { AxiosErrorResponse } from '@core/error.models';
import {
  Button,
  Column,
  ComponentType,
  createTableCell,
  createTableColumn,
  ModalBodyExtras,
  PaginatedTable,
  useModal,
} from '@gcl/react-core';
import { IProjectMasterScheduleOptionWithRoles } from '@masterSchedule/masterSchedule.models';
import { useNavigate } from '@routes/routes.hooks';
import { routes } from '@routes/routes.models';
import { UseQueryResult } from '@tanstack/react-query';
import * as React from 'react';
import { EDesignModificationIssueRole } from '../designModification/designModification.models';
import { ESelectT1CreationModalSearchParamKey, ESelectT1CreationModalType } from './SelectT1CreationModal.constants';

export interface IUseSelectT1CreationModalProps {
  type: ESelectT1CreationModalType;
  fetchT1OptionsFn: (
    useFetchOptionsProps: any,
  ) => UseQueryResult<IProjectMasterScheduleOptionWithRoles[], AxiosErrorResponse>;
  fetchT1OptionFnProps: { [key: string]: any };
}

export const useSelectT1CreationModal = ({
  type,
  fetchT1OptionFnProps,
  fetchT1OptionsFn,
}: IUseSelectT1CreationModalProps) => {
  const { openPortal, closePortal, renderModal } = useModal();
  const { navigate, generateRoutePath } = useNavigate();

  const { data: masterScheduleOptions } = fetchT1OptionsFn(fetchT1OptionFnProps);

  const masterScheduleTableColumns = React.useMemo(
    () => [
      createTableColumn({
        disableSortBy: true,
        accessor: 'id',
        Header: '',
        disableResizing: true,
        Cell: createTableCell<number, IProjectMasterScheduleOptionWithRoles>(
          (
            value: number,
            item: IProjectMasterScheduleOptionWithRoles,
            index: number,
            cellExtras: { [key: string]: any },
          ) => {
            if (type === ESelectT1CreationModalType.T3) {
              const issueRoles = getRole(item);

              return issueRoles.map((r) => (
                <Button
                  key={`gcl-gems-t3-creation-${index}-issue-role-${r}`}
                  className="gcl-mr-xs"
                  type={ComponentType.Success}
                  onClick={() => {
                    navigate(routes.t3.create.path, {}, true, undefined, {
                      [ESelectT1CreationModalSearchParamKey.masterScheduleId]: item.id,
                      [ESelectT1CreationModalSearchParamKey.issueRole]: r,
                    });
                  }}
                >
                  {getRoleText(r)}
                </Button>
              ));
            }

            return (
              <Button
                key={`gcl-gems-${type}-creation-${index}`}
                className="gcl-mr-xs"
                type={ComponentType.Success}
                onClick={() => {
                  navigate(
                    type === ESelectT1CreationModalType.T2 ? routes.t2.create.path : routes.t4.create.path,
                    {},
                    true,
                    undefined,
                    {
                      [ESelectT1CreationModalSearchParamKey.masterScheduleId]: item.id,
                    },
                  );
                }}
              >
                Select
              </Button>
            );
          },
        ),
      } as Column),
      createTableLinkWithOpenNewTabColumn<IProjectMasterScheduleOptionWithRoles>(
        'refNo',
        'T1 Ref No',
        ({ item }) => generateRoutePath(routes.t1.view.path, { id: item.id?.toString() }),
        ({ value, item, index, cellExtras }, link) => link,
      ),
      createTableColumn({
        Header: 'Design Package',
        accessor: 'designPackage',
        disableSortBy: true,
        disableResizing: true,
      } as Column),
    ],
    [],
  );

  const getRole = (masterScheduleItem: IProjectMasterScheduleOptionWithRoles) => {
    const issueRoles: EDesignModificationIssueRole[] = [];
    if (masterScheduleItem.isNominatedDesigner) issueRoles.push(EDesignModificationIssueRole.designer);
    if (masterScheduleItem.isNominatedSrp) issueRoles.push(EDesignModificationIssueRole.srp);
    if (masterScheduleItem.isNominatedTwc || masterScheduleItem.isNominatedTwcm)
      issueRoles.push(EDesignModificationIssueRole.twc);
    return issueRoles;
  };

  const getRoleText = (issueRole?: EDesignModificationIssueRole) => {
    switch (issueRole) {
      case EDesignModificationIssueRole.designer:
        return 'Designer';
      case EDesignModificationIssueRole.srp:
        return 'SRP';
      case EDesignModificationIssueRole.twc:
        return 'TWC';
      default:
        return '';
    }
  };

  const openCreationModal = (e: React.MouseEvent | undefined) => {
    openPortal(e);
  };

  const renderCreationModal = (headerComponent?: React.ReactElement) => {
    return renderModal({
      large: true,
      title: `Select T1`,
      onRenderBody: (bodyExtras?: ModalBodyExtras) => {
        return (
          <div className="gcl-gems-design-modification-creation-form-t1-table">
            {headerComponent}
            <PaginatedTable
              classNames={{ row: 'gcl-gems-design-modification-creation-form-t1-table-row' }}
              columns={masterScheduleTableColumns}
              items={masterScheduleOptions ?? []}
              disablePagination
              allowSelectRow={false}
            />
          </div>
        );
      },
      onRenderFooter: (onHide?: () => void, bodyExtras?: ModalBodyExtras) => {
        return (
          <Button
            prefixIconName="ChevronLeft"
            type={ComponentType.Secondary}
            onClick={() => {
              if (onHide) {
                onHide();
              }
            }}
            style={{ margin: '10px' }}
          >
            Cancel
          </Button>
        );
      },
    });
  };
  return {
    openPortal: openCreationModal,
    closePortal,
    renderModal: renderCreationModal,
  };
};
